import React, { type ReactNode, useMemo } from 'react';

import Lozenge from '@atlaskit/lozenge/lozenge';
import {
	isJiraProjectField,
	isSearchResultJiraIssue,
	isSearchResultJiraProject,
	type SearchJiraPartial,
	type SearchResultJiraIssue,
} from '@atlassian/dynamic-cards-search-client';
import { highlightMatchedText } from '@atlassian/dynamic-cards-search-common';

import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import SearchMetadataLink from '../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { MetaDataItemSkeleton } from '../../../../../../common/ui/search-result-skeleton';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignal } from '../../../../../../common/ui/social-signals';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import {
	MAX_CHARACTERS_IN_EXCERPT,
	truncateTextWithQuery,
} from '../../../../../../common/utils/truncate-text-with-query';
import { addQueryParams } from '../../../../../../common/utils/url-utils';
import {
	useBootstrap,
	useIsJiraHydrationLoading,
	usePrimaryProduct,
	useSearchQuery,
	useSearchSessionId,
	useSelectedProduct,
} from '../../../../../../controllers/store';

import { JiraProductIcon } from './icon';
import { ProjectMetadata } from './project-metadata';

const findProject = (partial: SearchResultJiraIssue) => {
	if (!partial.issue) {
		return;
	}

	for (const field of partial.issue.fieldsById.edges) {
		if (isJiraProjectField(field.node)) {
			return field.node;
		}
	}
};

const getIssueOrProjectKey = (partial: SearchJiraPartial) => {
	if (isSearchResultJiraIssue(partial)) {
		return partial.issue?.key;
	}
	if (isSearchResultJiraProject(partial)) {
		return partial.projectKey;
	}
	return null;
};

const addJiraQueryParams = (url: string, partial: SearchJiraPartial, searchSessionId: string) => {
	return addQueryParams(url, {
		searchObjectId: partial.id.split('/').pop(),
		searchContentType: partial.type,
		searchSessionId: searchSessionId,
	});
};

interface JiraSearchResultProps {
	partial: SearchJiraPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

const STATUS_REGEX = /[\s-]+/g;
/**
 * This logic would need to be updated to use the color passed from the BE and not the string.
 * https://product-fabric.atlassian.net/browse/QS-3655
 */
const getAppearance = (name: string) => {
	name = name.replace(STATUS_REGEX, '').toLowerCase();
	switch (true) {
		case ['todo', 'unavailable', 'minor', 'notstarted'].includes(name):
			return 'neutral';

		case ['available', 'completed', 'done', 'approved', 'resolved', 'added', 'closed'].includes(
			name,
		):
			return 'success';

		case ['error', 'declined', 'deleted', 'failed', 'rejected'].includes(name):
			return 'danger';

		case ['inprogress', 'open', 'modified'].includes(name):
			return 'information';
	}
	return 'neutral';
};

const getStatus = (name?: string, isLoading?: boolean) => {
	if (name) {
		const appearance = getAppearance(name);
		if (appearance) {
			return <Lozenge appearance={appearance}>{name}</Lozenge>;
		}
	}
	if (isLoading) {
		return <MetaDataItemSkeleton />;
	}
	return null;
};

type Content = {
	content?: Content[];
	text?: string;
	type: string;
};

const getContentString = (adf: Content, level: number = 0) => {
	let aggregatedString = '';

	if (adf.content) {
		adf.content.forEach((node) => {
			if (node.type === 'text') {
				aggregatedString += node.text;
			} else {
				if (node.content && level < 15) {
					aggregatedString += ' ' + getContentString(node, ++level);
				}
			}
		});
	}
	return aggregatedString;
};

const getDescription = (description: string, query: string) => {
	/**
	 * This description can be either a complicated JSON which is actually an ADF (Atlassian Document Format) schema of the entire content of the jira issue.
	 * or it could be a simple excerpt. This uncertainity is due to the progressive development of the BE feature and rolling it out.
	 */

	try {
		const adf = JSON.parse(description);
		const text = getContentString(adf);

		return truncateTextWithQuery(text, query);
	} catch (e) {
		return description.length > MAX_CHARACTERS_IN_EXCERPT
			? description.substring(0, MAX_CHARACTERS_IN_EXCERPT) + '...'
			: description.substring(0, MAX_CHARACTERS_IN_EXCERPT);
	}
};

export const JiraSearchResult = ({
	partial,
	position,
}: JiraSearchResultProps): React.JSX.Element => {
	const [searchSessionId] = useSearchSessionId();
	const [selectedProduct] = useSelectedProduct();
	const [searchQuery] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, isAdminHubAIEnabled }] = useBootstrap();
	const isHydrationLoading = useIsJiraHydrationLoading();
	const { description } = partial;

	let project = '';
	let Status: React.ReactNode = null;
	let url = partial.url;

	if (isSearchResultJiraIssue(partial)) {
		const projectField = findProject(partial);
		project = projectField?.project.name || '';
		const projectId = projectField?.project.id || '';

		url = addQueryParams(partial.url, {
			searchContainerId: projectId.split('/').pop(),
		});

		Status = getStatus(partial.status?.name, isHydrationLoading);
	}

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(partial.title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return partial.title;
		}
		return enrichExcerpt(highlightedTitle);
	}, [partial.title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = getDescription(description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	const getUrl = useMemo(() => {
		return addJiraQueryParams(url, partial, searchSessionId);
	}, [url, partial, searchSessionId]);

	const getKey = useMemo(() => {
		const value = getIssueOrProjectKey(partial);
		if (!value || !getUrl) {
			return null;
		}
		return <SearchMetadataLink label={value} href={getUrl} />;
	}, [partial, getUrl]);

	const getLastModified = useMemo(() => {
		if (!(isSearchResultJiraIssue(partial) || isSearchResultJiraProject(partial))) {
			return null;
		}
		if (partial.lastModifiedDate) {
			return <LastModified lastModified={partial.lastModifiedDate} />;
		}
		return null;
	}, [partial]);

	const metadataParts: [string, ReactNode][] = [
		['type', <SearchResultType type={partial.type} />],
		['key', getKey],
		project ? ['project', <ProjectMetadata project={project} />] : null,
		['lastModified', getLastModified],
		['status', Status],
	].filter(Boolean) as [string, ReactNode][];

	// fg('search-plex-jira-social-signal') => false
	if (isSearchResultJiraIssue(partial) && partial.socialSignal && false) {
		metadataParts.push(['social-signal', <BreadcrumbSocialSignal partial={partial as any} />]);
	}

	return (
		<SearchResult
			{...partial}
			primaryProduct={primaryProduct}
			url={getUrl}
			icon={<JiraProductIcon partial={partial} />}
			title={enrichedTitle}
			headerMetadata={
				<SearchMetadata product="jira" productSelected={!!selectedProduct} parts={metadataParts} />
			}
			description={enrichedDescription}
			showAISummary={partial.type === 'issue' && isAdminHubAIEnabled}
			cloudId={cloudId}
			position={position}
			partial={partial}
		/>
	);
};
